import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { ISchool } from '../interfaces/school.interface'
import { schoolGetClientSidedPropsUtil } from '../services/schoolGetClientSidedPropsUtil'
import PageLoadingComponent from '../components/LoadingComponents/PageLoadingComponent'

export interface SchoolContextState {
  loading: boolean
  error?: string
  schoolDetails?: ISchool | null
  host?: string
  refresh: () => Promise<void>
}

const SchoolContext = createContext<SchoolContextState | undefined>(undefined)

export const SchoolProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>(undefined)
  const [schoolDetails, setSchoolDetails] = useState<ISchool | null>(null)
  const [host, setHost] = useState<string | undefined>(undefined)

  const load = useCallback(async () => {
    setLoading(true)
    setError(undefined)
    try {
      const res = await schoolGetClientSidedPropsUtil()
      setHost(res.host)

      if (!res.schoolDetails) {
        if (res.error) {
          setError('SCHOOL_NOT_FOUND')
        }
        setSchoolDetails(null)
      } else {
        setSchoolDetails(res.schoolDetails)
      }
    } catch (err: any) {
      console.error('Error loading school details context', err)
      setError(err?.message || 'FAILED_TO_LOAD_SCHOOL')
      setSchoolDetails(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const value = useMemo<SchoolContextState>(
    () => ({
      loading,
      error,
      schoolDetails,
      host,
      refresh: load,
    }),
    [loading, error, schoolDetails, host, load],
  )

  return (
    <SchoolContext.Provider value={value}>
      {loading ? <PageLoadingComponent /> : children}
    </SchoolContext.Provider>
  )
}

export const useSchoolContext = (): SchoolContextState => {
  const context = useContext(SchoolContext)
  if (!context) {
    throw new Error('useSchoolContext must be used within a SchoolProvider')
  }
  return context
}

export default SchoolContext
